import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../src/Context/AuthContext";
import CardSection from '../Shared/CardSection';
import MonthlyCommissionChart from './Charts/MonthlyCommissionChart';
import NewBusinessStatusChart from './Charts/NewBusinessStatusChart';
import FilterIcon from '../../SVG/Filter';
import Loading from '../Shared/Loading';

const AgentValuesSection = ({ model }) => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const apiURL = import.meta.env.VITE_API;

    // We only use model for initial agent info + downlines
    const initialAgtCode = model?.agtCode || '';
    const selfDisplayName = model?.agentInfo?.displayName || 'You';

    const downlines = useMemo(() => {
        const list = model?.downlines || [];
        return [...list].sort((a, b) =>
            a.agentCode?.localeCompare(b.agentCode)
        );
    }, [model?.downlines]);

    // Entire agent "values" payload returned from GetAgentValues
    const [agentModel, setAgentModel] = useState(null);
    const [selectedAgtCode, setSelectedAgtCode] = useState(initialAgtCode);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const formatZip = (zip) => {
        if (!zip) return "";

        const onlyDigits = zip.trim().replace(/\D/g, "");
        if (onlyDigits.length === 9) {
            return onlyDigits.slice(0, 5) + "-" + onlyDigits.slice(5);
        }
        if (onlyDigits.length === 5) {
            return onlyDigits;
        }
        return zip;
    };

    // Helper to load agent values from API
    const loadAgentValues = async (agtCode) => {
        if (!agtCode) {
            setAgentModel(null);
            return;
        }

        try {
            setLoading(true);
            setError('');

            const res = await fetch(
                `${apiURL}/agent/GetAgentValues?agtCode=${encodeURIComponent(agtCode)}`,
                {
                    method: 'GET',
                    credentials: 'include'
                }
            );

            if (res.status === 401) {
                logout();
                navigate('/login');
                throw new Error('unauthorized');
            }

            if (res.status === 403) {
                navigate('/home');
                throw new Error('forbidden');
            }

            if (!res.ok) {
                const text = await res.text();
                console.error('GetAgentValues failed:', text);
                throw new Error('Failed to load agent values');
            }

            const data = await res.json();
            setAgentModel(data);
        } catch (err) {
            console.error('Error loading agent values:', err);
            setError('Unable to load agent values.');
            setAgentModel(null);
        } finally {
            setLoading(false);
        }
    };

    // Initial load for the logged-in agent
    useEffect(() => {
        if (initialAgtCode) {
            loadAgentValues(initialAgtCode);
        }
    }, [initialAgtCode]);

    const handleDownlineChange = async (e) => {
        const agtCode = e.target.value;
        setSelectedAgtCode(agtCode);
        await loadAgentValues(agtCode);
    };

    const AddrLine1 = agentModel?.agentInfo?.addr;
    const AddrLine2 = agentModel?.agentInfo?.addr2;
    const AddrLine3 = agentModel?.agentInfo
        ? `${agentModel.agentInfo.city} ${agentModel.agentInfo.state}, ${formatZip(agentModel.agentInfo.zip)}`
        : "";

    const fullAddress = (
        <>
            {AddrLine1 && <>{AddrLine1}<br /></>}
            {AddrLine2 && <>{AddrLine2}<br /></>}
            {AddrLine3 && <>{AddrLine3}</>}
        </>
    );

    if (loading && !agentModel) {
        return <Loading />;
    }

    return (
        <div className="container container-section custom-width" id="agent-values-container">

            {loading && (
                <div className="loading-overlay">
                    <Loading />
                </div>
            )}

            {/* Filter Box */}
            <div className="filter-box">
                <FilterIcon />
                <div className="filter-item">
                    <label htmlFor="downlineSelect" className="filterLabel">Downline</label>
                    <select
                        id="downlineSelect"
                        className="filterInput"
                        value={selectedAgtCode}
                        onChange={handleDownlineChange}
                    >
                        {/* Self option � still from the original model */}
                        {initialAgtCode && (
                            <option value={initialAgtCode}>
                                {initialAgtCode} - {selfDisplayName}
                            </option>
                        )}

                        {/* Downlines */}
                        {downlines.map((dl, i) => (
                            <option key={i} value={dl.agentCode}>
                                {dl.agentCode} - {dl.name ?? dl.displayName ?? "Downline"}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                    {error}
                </div>
            )}

            {/* If we don't yet have an agentModel, just show a simple message */}
            {!loading && !agentModel ? (
                <p>No agent values available.</p>
            ) : (
                <>
                    {/* Top Row: Summary Cards */}
                    <div className="flex-row">
                        {[
                            { label: "Total Commission", value: agentModel.agentStats?.paidTotal },
                            { label: "YTD Commission", value: agentModel.agentStats?.paidYTD },
                            {
                                label: "Highest Monthly Commission - " + (agentModel.agentStats?.highestMonth || ""),
                                value: agentModel.agentStats?.highestMonthlyPaid
                            }
                        ].map(({ label, value }, idx) => (
                            <div key={idx} className="policy-card small-card">
                                <div className="card">
                                    <div className="card-body">
                                        <h6>{label}</h6>
                                        <h3 className="bold value">{value}</h3>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Second Row: Charts + Agent Details */}
                    <div className="flex-row">
                        <div className="policy-card large-card">
                            <div className="card">
                                <div className="card-header large-card-header">
                                    <h6>New Business - Past 6 Months</h6>
                                </div>
                                <div className="card-body">
                                    <NewBusinessStatusChart
                                        data={agentModel.agentStats?.recentPolStatus || []}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="policy-card large-card">
                            <div className="card">
                                <div className="card-header large-card-header">
                                    <h6>Monthly Commission Total</h6>
                                </div>
                                <div className="card-body">
                                    <MonthlyCommissionChart
                                        data={agentModel.agentStats?.last12Months || []}
                                    />
                                </div>
                            </div>
                        </div>

                        <CardSection
                            title="Agent Details"
                            blocks={[
                                { label: "Name:", value: agentModel.agentInfo?.displayName },
                                { label: "Office Phone:", value: agentModel.agentInfo?.officePhone },
                                //{ label: "Home Phone:", value: agentModel.agentInfo?.homePhone },
                                { label: "Cell Phone:", value: agentModel.agentInfo?.cellPhone },
                                { label: "Email:", value: agentModel.agentInfo?.email },
                                //{ label: "Bank Account Number:", value: agentModel.agentInfo?.accountNumber },
                                //{ label: "Routing Number", value: agentModel.agentInfo?.routingNumber },
                                { label: "Address:", value: fullAddress },
                                { label: "2FA Email:", value: agentModel.agentInfo?.twoFactorEmail },
                            ]}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default AgentValuesSection;
